// contexts/AccessibilityContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

// Accessibility Context
const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  // =========================
  // EXISTING FEATURES (KEEP)
  // =========================

  const [contrastMode, setContrastMode] = useState(() => {
    const saved = localStorage.getItem('accessibility-contrast-mode');
    return saved || 'default';
  });

  const [textSize, setTextSize] = useState(() => {
    const saved = localStorage.getItem('accessibility-text-size');
    return saved || 'small';
  });

  // New accessibility features for visually impaired users
  const [isScreenReaderEnabled, setIsScreenReaderEnabled] = useState(() => {
    const saved = localStorage.getItem('accessibility-screen-reader');
    return saved ? JSON.parse(saved) : false;
  });

  const [isVoiceCommandsEnabled, setIsVoiceCommandsEnabled] = useState(() => {
    const saved = localStorage.getItem('accessibility-voice-commands');
    return saved ? JSON.parse(saved) : false;
  });

  const [voiceType, setVoiceType] = useState(() => {
    const saved = localStorage.getItem('accessibility-voice-type');
    return saved || 'default';
  });

  const [isListening, setIsListening] = useState(false);
  const [isReading, setIsReading] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedContrast = localStorage.getItem('accessibility-contrast-mode');
    const savedTextSize = localStorage.getItem('accessibility-text-size');

    if (savedContrast) {
      setContrastMode(savedContrast);
    }
    if (savedTextSize) {
      setTextSize(savedTextSize);
    }
  }, []);

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('accessibility-contrast-mode', contrastMode);
    document.documentElement.setAttribute('data-contrast', contrastMode);
  }, [contrastMode]);

  useEffect(() => {
    localStorage.setItem('accessibility-text-size', textSize);
    document.documentElement.setAttribute('data-text-size', textSize);
  }, [textSize]);

  useEffect(() => {
    localStorage.setItem('accessibility-screen-reader', JSON.stringify(isScreenReaderEnabled));
  }, [isScreenReaderEnabled]);

  useEffect(() => {
    localStorage.setItem('accessibility-voice-commands', JSON.stringify(isVoiceCommandsEnabled));
  }, [isVoiceCommandsEnabled]);

  useEffect(() => {
    localStorage.setItem('accessibility-voice-type', voiceType);
  }, [voiceType]);

  // Apply contrast mode to body class
  useEffect(() => {
    document.body.className = document.body.className
      .replace(/contrast-\w+/g, '')
      .trim();
    document.body.classList.add(`contrast-${contrastMode}`);
  }, [contrastMode]);

  // Apply text size to root font size
  useEffect(() => {
    const multipliers = {
      small: 0.875,
      default: 1,
      large: 1.125,
    };
    const multiplier = multipliers[textSize] || 1;
    document.documentElement.style.fontSize = `${16 * multiplier}px`;
  }, [textSize]);

  // Existing functions
  const toggleContrastMode = useCallback(() => {
    setContrastMode(prev => (prev === 'default' ? 'high-contrast' : 'default'));
  }, []);

  const setTextSizeMode = useCallback((size) => {
    setTextSize(size);
  }, []);

  // =========================
  // SPEECH / SCREEN READER
  // =========================

  // Helper function to get appropriate voice based on voice type
  const getSpeechVoice = useCallback((voiceTypeParam) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return null;
    }

    const voices = window.speechSynthesis.getVoices();
    const type = voiceTypeParam || voiceType;


    switch (type) {
      case 'female':
        return (
          voices.find(voice =>
            voice.name.toLowerCase().includes('female') ||
            voice.name.toLowerCase().includes('woman') ||
            voice.name.toLowerCase().includes('zira') ||
            voice.name.toLowerCase().includes('susan')
          ) || voices.find(voice => voice.lang.startsWith('en'))
        );
      case 'male':
        return (
          voices.find(voice =>
            voice.name.toLowerCase().includes('male') ||
            voice.name.toLowerCase().includes('man') ||
            voice.name.toLowerCase().includes('david') ||
            voice.name.toLowerCase().includes('tom')
          ) || voices.find(voice => voice.lang.startsWith('en'))
        );
      case 'slow':
      case 'fast':
      default:
        return (
          voices.find(voice => voice.default) ||
          voices.find(voice => voice.lang.startsWith('en')) ||
          voices[0]
        );
    }
  }, [voiceType]);

  // Enhanced announcement / reading function with voice selection
  const announceWithVoice = useCallback(
    (message, customVoiceType = null) => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        const voiceTypeToUse = customVoiceType || voiceType;
        const selectedVoice = getSpeechVoice(voiceTypeToUse);

        const utterance = new SpeechSynthesisUtterance(message);

        if (selectedVoice) {
          utterance.voice = selectedVoice;
        }

        switch (voiceTypeToUse) {
          case 'slow':
            utterance.rate = 0.7;
            utterance.pitch = 0.9;
            break;
          case 'fast':
            utterance.rate = 1.3;
            utterance.pitch = 1.2;
            break;
          default:
            utterance.rate = 1.0;
        }

        utterance.pitch = 1.1;
        utterance.volume = 0.9;

        utterance.onend = () => {
          setIsReading(false);
        };
        utterance.onerror = () => {
          setIsReading(false);
        };

        // cancel anything currently reading
        window.speechSynthesis.cancel();
        setIsReading(true);

        if (window.speechSynthesis.getVoices().length === 0) {
          window.speechSynthesis.addEventListener(
            'voiceschanged',
            () => {
              const updatedVoice = getSpeechVoice(voiceTypeToUse);
              if (updatedVoice) {
                utterance.voice = updatedVoice;
              }
              window.speechSynthesis.speak(utterance);
            },
            { once: true }
          );
        } else {
          window.speechSynthesis.speak(utterance);
        }
      }
    },
    [voiceType, getSpeechVoice]
  );

  // Helper: capture main text of current page (works on all routes)
  const getCurrentPageText = useCallback(() => {
    if (typeof document === 'undefined') return '';

    // Prefer main or landmarks first
    const candidates = [
      '[data-main-content="true"]',
      'main[role="main"]',
      'main',
      '#main',
      '#page-content',
    ];

    let text = '';
    for (const selector of candidates) {
      const el = document.querySelector(selector);
      if (el && el.innerText && el.innerText.trim().length > 50) {
        text = el.innerText;
        break;
      }
    }

    if (!text.trim()) {
      text = document.body?.innerText || '';
    }

    return text.trim();
  }, []);

  // Public: read the current page
  const readPageContent = useCallback(() => {
    if (!isScreenReaderEnabled) {
      announceWithVoice('Screen reader is disabled. Please enable it first.');
      return;
    }

    const text = getCurrentPageText();
    if (!text) {
      announceWithVoice('Sorry, there is no readable content on this page.');
      return;
    }

    announceWithVoice(text);
  }, [isScreenReaderEnabled, getCurrentPageText, announceWithVoice]);

  const stopReading = useCallback(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    setIsReading(false);
  }, []);


  // =========================
  // TOGGLES (SCREEN READER / VOICE CMD)
  // =========================

  const toggleScreenReader = useCallback(() => {
    const newState = !isScreenReaderEnabled;
    setIsScreenReaderEnabled(newState);

    const message = newState ? 'Screen reader enabled' : 'Screen reader disabled';
    announceWithVoice(message);
  }, [isScreenReaderEnabled, announceWithVoice]);

  const toggleVoiceCommands = useCallback(() => {
    const newState = !isVoiceCommandsEnabled;
    setIsVoiceCommandsEnabled(newState);

    const message = newState ? 'Voice commands enabled' : 'Voice commands disabled';
    announceWithVoice(message);
  }, [isVoiceCommandsEnabled, announceWithVoice]);

  const updateVoiceType = useCallback(
    (newVoiceType) => {
      setVoiceType(newVoiceType);

      if (isScreenReaderEnabled) {
        const voiceMessages = {
          default: 'Default voice selected',
          female: 'Female voice selected',
          male: 'Male voice selected',
          slow: 'Slow speech selected',
          fast: 'Fast speech selected',
        };

        const message = voiceMessages[newVoiceType] || 'Voice setting changed';
        announceWithVoice(message, newVoiceType);
      }
    },
    [isScreenReaderEnabled, announceWithVoice]
  );

  // =========================
  // VOICE COMMANDS (NAVIGATION ETC.)
  // =========================

  const navigateTo = useCallback((path) => {
    if (typeof window === 'undefined') return;
    // Simple SPA-friendly navigation
    window.location.href = path;
  }, []);

  const handleVoiceCommand = useCallback(
    (rawCommand) => {
      const command = rawCommand.toLowerCase().trim();

      // --- READING / STOP ---
      if (command.includes('read')) {
        readPageContent();
        return;
      }

      if (command.includes('stop')) {
        stopReading();
        return;
      }

      // --- TEXT SIZE ---
      if (
        command.includes('bigger text') ||
        command.includes('increase text') ||
        command.includes('large text')
      ) {
        setTextSizeMode('large');
        announceWithVoice('Text size increased');
        return;
      }

      if (
        command.includes('smaller text') ||
        command.includes('decrease text') ||
        command.includes('small text')
      ) {
        setTextSizeMode('small');
        announceWithVoice('Text size decreased');
        return;
      }

      if (command.includes('normal text') || command.includes('default text')) {
        setTextSizeMode('default');
        announceWithVoice('Text size reset to default');
        return;
      }

      // --- CONTRAST ---
      if (command.includes('high contrast')) {
        if (contrastMode !== 'high-contrast') {
          setContrastMode('high-contrast');
          announceWithVoice('High contrast mode enabled');
        } else {
          announceWithVoice('High contrast is already enabled');
        }
        return;
      }

      if (command.includes('normal contrast') || command.includes('default contrast')) {
        if (contrastMode !== 'default') {
          setContrastMode('default');
          announceWithVoice('Contrast reset to default');
        } else {
          announceWithVoice('Default contrast is already active');
        }
        return;
      }

      // --- SCROLLING ---
      if (command.includes('scroll down')) {
        window.scrollBy({ top: window.innerHeight, left: 0, behavior: 'smooth' });
        return;
      }

      if (command.includes('scroll up')) {
        window.scrollBy({ top: -window.innerHeight, left: 0, behavior: 'smooth' });
        return;
      }

      if (command.includes('top of page') || command.includes('go to top')) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        return;
      }

      // --- NAVIGATION based on your menu ---

      // Home
      if (command.includes('home')) {
        navigateTo('/');
        return;
      }


      // Who We Are / About
      if (command.includes('who we are')) {
        navigateTo('/whoweare');
        return;
      }

      if (command.includes('about')) {
        navigateTo('/whoweare');
        return;
      }

      // Mission, Vision & Values
      if (
        command.includes('mission') ||
        command.includes('vision') ||
        command.includes('values')
      ) {
        navigateTo('/about/mission-vision-values');
        return;
      }

      // Leadership
      if (command.includes('leadership')) {
        navigateTo('/leadership');
        return;
      }

      // Annual Report
      if (command.includes('annual report') || command.includes('reports')) {
        navigateTo('/annual-report');
        return;
      }

      // Programs
      if (command.includes('program') || command.includes('programs')) {
        navigateTo('/programs');
        return;
      }

      // Gallery
      if (command.includes('gallery')) {
        navigateTo('/gallery');
        return;
      }

      // Partners
      if (command.includes('partners')) {
        navigateTo('/partners');
        return;
      }

      // News
      if (command.includes('news') || command.includes('updates')) {
        navigateTo('/news');
        return;
      }

      // Contact Us
      if (command.includes('contact')) {
        navigateTo('/contactus');
        return;
      }

      // Donate
      if (command.includes('donate') || command.includes('donation')) {
        navigateTo('/donate');
        return;
      }

      // Login
      if (command.includes('login') || command.includes('log in') || command.includes('sign in')) {
        navigateTo('/login');
        return;
      }

      // If nothing matched
      announceWithVoice('Sorry, I did not understand that command.');
    },
    [
      announceWithVoice,
      readPageContent,
      stopReading,
      setTextSizeMode,
      contrastMode,
      navigateTo,
      setContrastMode,
    ]
  );

  const startVoiceRecognition = useCallback(() => {
    if (!isVoiceCommandsEnabled) {
      announceWithVoice('Voice commands are disabled. Please enable them first.');
      return;
    }

    if (typeof window === 'undefined') return;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Voice recognition is not supported in this browser. Please use Google Chrome.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US'; // later can depend on current language
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = () => {
      setIsListening(false);
      announceWithVoice('Sorry, there was a problem understanding your voice.');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      handleVoiceCommand(transcript);
    };

    recognition.start();
  }, [isVoiceCommandsEnabled, announceWithVoice, handleVoiceCommand]);

  // =========================
  // CONTEXT VALUE
  // =========================

  const value = {
    // Existing accessibility features
    contrastMode,
    textSize,
    toggleContrastMode,
    setTextSizeMode,

    // Screen reader & voice settings
    isScreenReaderEnabled,
    isVoiceCommandsEnabled,
    voiceType,
    isListening,
    isReading,

    toggleScreenReader,
    toggleVoiceCommands,
    updateVoiceType,
    getSpeechVoice,
    announceWithVoice,
    setIsListening,

    // New helpers
    readPageContent,
    stopReading,
    startVoiceRecognition,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};
